import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, RotateCcw, Loader2, Upload } from 'lucide-react';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  fieldStyle, navyBtn, outlineBtn, selectStyle,
} from '../../styles/tokens';
import client from '../../api/client';
import { useOptions } from '../../context/OptionsContext';
import BulkUploadModal from '../shared/BulkUploadModal';
import { usePaginatedSort } from '../../shared/hooks/usePaginatedSort';
import PaginationBar from '../../shared/components/PaginationBar';
import SortableHeader from '../../shared/components/SortableHeader';

export default function ProjectManagement() {
  const navigate = useNavigate();
  const { supervisors, refresh: refreshOptions } = useOptions();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState('active');
  const [showBulk, setShowBulk]           = useState(false);

  useEffect(() => { fetchItems(); }, []);

  async function fetchItems() {
    try {
      const res = await client.get('/api/admin/projects');
      setItems(res.data);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeactivate(id) {
    try {
      await client.delete(`/api/admin/projects/${id}`);
      await fetchItems();
      refreshOptions();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  }

  async function handleReactivate(id) {
    try {
      await client.patch(`/api/admin/projects/${id}`, { active: true });
      await fetchItems();
      refreshOptions();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al reactivar');
    }
  }

  // V30.1: ganga roles can have multiple members — render each name on its own line.
  function renderMemberNames(names, fallbackSingle) {
    const list = (names && names.length ? names : [fallbackSingle]).filter(Boolean);
    if (list.length === 0) return '—';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {list.map(n => <span key={n}>{n}</span>)}
      </div>
    );
  }

  const term = search.toLowerCase();
  const filteredItems = items.filter(p => {
    const match = p.name.toLowerCase().includes(term) || (p.code || '').toLowerCase().includes(term) || (p.client || '').toLowerCase().includes(term);
    if (!match) return false;
    if (statusFilter === 'active') return p.active;
    if (statusFilter === 'inactive') return !p.active;
    return true;
  });
  const activeCount = items.filter(p => p.active).length;

  // V29.2: hook MUST run on every render (before any early return) so hook order stays stable
  const {
    pageItems, page, pageSize, totalPages, totalItems,
    sortKey, sortDir, toggleSort, setPage, setPageSize,
  } = usePaginatedSort(filteredItems, { defaultSortKey: 'name' });

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={20} className="spin" /> Cargando proyectos…
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0 }}>
          Proyectos
          <span style={{ fontSize: 13, fontWeight: '400', color: '#94a3b8', marginLeft: 8 }}>
            ({activeCount} activos)
          </span>
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowBulk(true)} style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6, background: '#475569' }}>
            <Upload size={14} /> Carga Masiva
          </button>
          <button onClick={() => navigate('/admin/proyecto/nuevo')} style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Agregar Proyecto
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar proyecto..."
          style={{ ...fieldStyle, width: 240 }}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...selectStyle, width: 'auto' }}>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
          <option value="all">Todos</option>
        </select>
      </div>

      {/* Items table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${BORDER_COLOR}` }}>
              {[
                { label: 'Nombre',        key: 'name' },
                { label: 'Código',        key: 'code' },
                { label: 'Tipo',          key: 'project_type' },
                { label: 'Aprobación',    key: 'approval_type' },
                { label: 'Supervisor(a)', key: 'supervisorName' },
                { label: 'Ingeniero(a)',  key: 'engineerName' },
                { label: 'Gerente',       key: 'managerName' },
                // V62 — desde el listado no se veía quién cierra la cadena de PTO.
                { label: 'Socio(a)',      key: 'socioName' },
                { label: 'Rev. Final',    key: null },
                { label: 'Estado',        key: 'active' },
                { label: '',              key: null },
              ].map(h => (
                <th key={h.label || 'actions'} style={{
                  padding: '8px 10px', fontSize: 11, fontWeight: '700', color: '#64748b',
                  textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left',
                  whiteSpace: 'nowrap',
                }}>
                  <SortableHeader label={h.label} sortKey={h.key} currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageItems.map(item => {
              const dim = !item.active;
              const txt = dim ? '#94a3b8' : '#475569';
              const isAdmin = item.project_type === 'administrative' || item.isAdminProject;
              return (
                <tr key={item.id} style={{
                  borderBottom: `1px solid ${BORDER_COLOR}`,
                  opacity: dim ? 0.6 : 1,
                  cursor: item.active ? 'pointer' : 'default',
                }} onClick={() => item.active && navigate(`/admin/proyecto/${item.id}`)}>
                  <td style={{ padding: '10px', fontWeight: '600', color: dim ? '#94a3b8' : NAVY, textDecoration: dim ? 'line-through' : 'none' }}>
                    {item.name}
                  </td>
                  <td style={{ padding: '10px', fontSize: 11, fontWeight: '600', fontFamily: 'monospace', color: dim ? '#94a3b8' : AMBER }}>
                    {item.code || '—'}
                  </td>
                  <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: '600',
                      background: isAdmin ? '#ede9fe' : '#e0f2fe',
                      color: isAdmin ? '#6d28d9' : '#0369a1',
                    }}>
                      {isAdmin ? 'Admin' : 'Proyecto'}
                    </span>
                    {item.is_testing && (
                      <span title="Oculto de tableros, reportes y exportaciones" style={{
                        display: 'inline-block', marginLeft: 6, padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: '600',
                        background: '#fef3c7', color: '#92400e',
                      }}>
                        Prueba
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px', fontSize: 12, color: txt, whiteSpace: 'nowrap' }}>
                    {item.approval_type === '3_levels' ? '3 Niveles' : '2 Niveles'}
                  </td>
                  <td style={{ padding: '10px', fontSize: 12, color: txt }}>{renderMemberNames(item.supervisorNames, item.supervisorName)}</td>
                  <td style={{ padding: '10px', fontSize: 12, color: txt }}>{renderMemberNames(item.engineerNames,   item.engineerName)}</td>
                  <td style={{ padding: '10px', fontSize: 12, color: txt }}>{renderMemberNames(item.managerNames,    item.managerName)}</td>
                  <td style={{ padding: '10px', fontSize: 12, color: txt }}>{renderMemberNames(item.socioNames,      null)}</td>
                  <td style={{ padding: '10px', fontSize: 12, color: txt }}>
                    {isAdmin ? (item.finalReviewerName || '—') : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {item.nominaReviewerName && <span title="Nómina" style={{ fontSize: 11 }}><strong>N:</strong> {item.nominaReviewerName}</span>}
                        {item.expensesReviewerName && <span title="Gastos" style={{ fontSize: 11 }}><strong>G:</strong> {item.expensesReviewerName}</span>}
                        {!item.nominaReviewerName && !item.expensesReviewerName && '—'}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: '600',
                      background: item.active ? '#f0fdf4' : '#fef2f2',
                      color: item.active ? '#166534' : '#991b1b',
                    }}>
                      {item.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td style={{ padding: '10px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      {item.active ? (
                        <>
                          <button onClick={() => navigate(`/admin/proyecto/${item.id}`)} style={iconBtn} title="Editar">
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => handleDeactivate(item.id)} style={iconBtn} title="Desactivar">
                            <Trash2 size={13} style={{ color: '#dc2626' }} />
                          </button>
                        </>
                      ) : (
                        <button onClick={() => handleReactivate(item.id)} style={iconBtn} title="Reactivar">
                          <RotateCcw size={13} style={{ color: '#16a34a' }} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredItems.length === 0 && (
              <tr>
                {/* 11 = 10 + la columna Socio(a) de V62. Si se agrega otra, mover esto también. */}
                <td colSpan={11} style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  No se encontraron proyectos
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={page} pageSize={pageSize} totalPages={totalPages} totalItems={totalItems}
        onPageChange={setPage} onPageSizeChange={setPageSize}
      />

      <BulkUploadModal
        open={showBulk}
        onClose={() => setShowBulk(false)}
        onSuccess={fetchItems}
        title="Carga Masiva de Proyectos"
        endpoint="/api/admin/projects/bulk"
        templateColumns={['name', 'code', 'client', 'approval_type', 'project_type']}
        templateExample={[
          ['Proyecto Alpha', 'PROJ-001', 'Cliente ABC', '2_levels', 'project'],
          ['Oficina Admin', 'ADM-001', '', '2_levels', 'administrative'],
        ]}
      />
    </div>
  );
}

const iconBtn = {
  background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
  padding: '6px 8px', cursor: 'pointer', color: NAVY, display: 'inline-flex', alignItems: 'center',
};
